import { Category } from '@wix/answers-api/dist/kb';
import { Box, Heading, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { TimerSmall } from '@wix/wix-ui-icons-common';
import { FunctionComponent } from 'react';
import { DATA_HOOKS } from '../../../dataHooks';
import { ArticleSearchResultItem } from '../../../types';
import { COLORS } from '../../../utils/theme';
import { InnerLink } from '../../InnerLink';
import { SearchResultItemBadges } from './SearchResultItemBadges';
import css from './index.module.scss';
import { RenderHTML } from '../../RenderHTML';

export type SearchResultItemProps = ArticleSearchResultItem & {
  onArticleTopicBadgeClick: (rootTopic: Category) => void;
  onArticleResolutionBadgeClick: (clickedText: string, url: string) => void;
  onArticleClick: () => void;
  rootTopic: Category;
};

export const SearchResultItem: FunctionComponent<SearchResultItemProps> = ({
  title,
  uri,
  description,
  type,
  resolution,
  resolutionEta,
  readingTimeInMinutes,
  rootTopic,
  onArticleTopicBadgeClick,
  onArticleResolutionBadgeClick,
  onArticleClick,
}) => {
  const { t } = useTranslation();

  return (
    <Box
      className={css.searchResultItem}
      direction="vertical"
      align="left"
      dataHook={DATA_HOOKS.SEARCH_RESULT_ITEM}
    >
      <SearchResultItemBadges
        rootTopic={rootTopic}
        type={type}
        resolution={resolution}
        resolutionEta={resolutionEta}
        onArticleTopicBadgeClick={onArticleTopicBadgeClick}
        onArticleResolutionBadgeClick={onArticleResolutionBadgeClick}
      />
      <InnerLink href={uri} className={css.titleLink} onClick={onArticleClick} dataHook={DATA_HOOKS.SEARCH_RESULT_ITEM_TITLE_LINK}>
        <Heading
          color={COLORS.D10}
          className={css.title}
          appearance="H4"
          dataHook={DATA_HOOKS.SEARCH_RESULT_ITEM_TITLE}
        >
          {title}
        </Heading>
      </InnerLink>
      <Box
        className={css.descriptionAndReadingTimeWrapper}
        direction="horizontal"
        verticalAlign="middle"
        align="space-between"
      >
        <InnerLink
          href={uri}
          className={css.descriptionLink}
          onClick={onArticleClick}
        >
          <RenderHTML
            html={description}
            textElement={{
              color: COLORS.D10,
              weight: 'thin',
              size: 'medium',
              maxLines: 2,
              ellipsis: true,
              className: css.description,
              tooltipProps: { enterDelay: 250 },
              dataHook: DATA_HOOKS.SEARCH_RESULT_ITEM_DESCRIPTION,
            }}
          />
        </InnerLink>
        <Box className={css.readingTimeInMinutes} verticalAlign="middle">
          <TimerSmall color={COLORS.D40} className={css.timerIcon} />
          <Text
            size="tiny"
            color={COLORS.D40}
            className={css.timeToRead}
            dataHook={DATA_HOOKS.SEARCH_RESULT_ITEM_TIME_TO_READ}
          >
            {t('informative-article-time-to-read', {
              time: readingTimeInMinutes,
            })}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
